import { auth } from './firebase';

/**
 * Send an email when a user's trial has ended
 * @param to Email address of the recipient
 */
export async function sendTrialEndedEmail(to: string): Promise<any> {
  try {
    // Use the Netlify function URL directly
    const baseUrl = window.location.origin;
    
    console.log('Sending email using base URL:', baseUrl);
    
    // Make sure we're using the correct function path
    const functionUrl = `${baseUrl}/.netlify/functions/sendTrialEndedEmail`;
    console.log('Function URL:', functionUrl);
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ 
        email: to,
        timestamp: new Date().toISOString() // Add timestamp for debugging
      })
    });

    console.log('Response status:', response.status);
    
    if (!response.ok) {
      let errorMessage = 'Failed to send email';
      const responseText = await response.text();
      console.log('Error response text:', responseText);

      try {
        const errorData = JSON.parse(responseText);
        if (errorData.error) {
          errorMessage = `${errorData.error}${errorData.details ? ': ' + errorData.details : ''}`;
        }
      } catch (jsonError) {
        // If JSON parsing fails, use the raw response text
        errorMessage = responseText || `Server error: ${response.status} ${response.statusText}`;
      }

      throw new Error(`Email sending failed: ${errorMessage}`);
    }

    // Parse and return the response data
    const data = await response.json();
    console.log('Email sent successfully:', data);
    return data;
  } catch (error: any) {
    console.error('Error sending trial ended email:', error);
    // Always throw a user-friendly error with detailed message
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'Failed to send email: Network or server error';
    
    throw new Error(errorMessage);
  }
}

/**
 * Send a reminder email when a user's trial is about to end
 * @param to Email address of the recipient
 * @param daysLeft Number of days left in the trial
 */
export async function sendTrialReminderEmail(to: string, daysLeft: number): Promise<void> {
  try {
    // Implement this when needed
    console.log('Trial reminder email sent successfully');
  } catch (error) {
    console.error('Error sending trial reminder email:', error);
    throw error;
  }
}

/**
 * Send a confirmation email when a user subscribes to premium
 * @param to Email address of the recipient
 */
export async function sendSubscriptionConfirmationEmail(to: string): Promise<void> {
  try {
    // Implement this when needed
    console.log('Subscription confirmation email sent successfully');
  } catch (error) {
    console.error('Error sending subscription confirmation email:', error);
    throw error;
  }
}

/**
 * Send a verification code email for account verification
 * @param to Email address of the recipient
 * @param code Verification code
 */
export async function sendVerificationEmail(to: string, code: string): Promise<void> {
  try {
    // Implement this when needed
    console.log('Verification email sent successfully');
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw error;
  }
}

/**
 * Send a password reset email
 * @param to Email address of the recipient
 * @param resetLink Password reset link
 */
export async function sendPasswordResetEmail(to: string, resetLink: string): Promise<void> {
  try {
    // Implement this when needed
    console.log('Password reset email sent successfully');
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
}

/**
 * Send a welcome email to new users
 * @param to Email address of the recipient
 * @param name User's name
 */
export async function sendWelcomeEmail(to: string, name: string): Promise<void> {
  try {
    // Implement this when needed
    console.log('Welcome email sent successfully');
  } catch (error) {
    console.error('Error sending welcome email:', error);
    throw error;
  }
}

/**
 * Send account deletion confirmation email
 * @param to Email address of the recipient
 */
export async function sendAccountDeletionEmail(to: string): Promise<any> {
  try {
    const baseUrl = window.location.origin;
    const functionUrl = `${baseUrl}/.netlify/functions/sendAccountDeletionEmail`;

    console.log('Sending account deletion email to:', to);

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        email: to,
        timestamp: new Date().toISOString()
      })
    });

    console.log('Response status:', response.status);

    if (!response.ok) {
      let errorMessage = 'Failed to send email';
      const responseText = await response.text();
      console.log('Error response text:', responseText);

      try {
        const errorData = JSON.parse(responseText);
        if (errorData.error) {
          errorMessage = `${errorData.error}${errorData.details ? ': ' + errorData.details : ''}`;
        }
      } catch (jsonError) {
        errorMessage = responseText || `Server error: ${response.status} ${response.statusText}`;
      }

      throw new Error(`Email sending failed: ${errorMessage}`);
    }

    const data = await response.json();
    console.log('Account deletion email sent successfully:', data);
    return data;
  } catch (error: any) {
    console.error('Error sending account deletion email:', error);
    const errorMessage = error instanceof Error
      ? error.message
      : 'Failed to send email: Network or server error';

    throw new Error(errorMessage);
  }
}